import { useEffect } from 'react'

import { Box } from '@factorialco/deprecated-design-system'
import {
  type CustomVisualizationProps,
  useDataCollectionData,
} from '@factorialco/f0-react/dist/experimental'
import { type FormInsightsOverviewElementFragment } from 'generated/resources/graphql'
import groupBy from 'lodash/groupBy'
import sortBy from 'lodash/sortBy'

import { useFormInsightsDataSource } from 'modules/engagement_surveys/components/FormInsightsOverview/useFormInsightsDataSource'
import type { FormFiltersStorage } from 'modules/forms'

import { LongText } from './ElementTypeRenderers/LongText'
import { Number as NumberElement } from './ElementTypeRenderers/Number'
import { Rating } from './ElementTypeRenderers/Rating'
import { Section } from './ElementTypeRenderers/Section'
import { Select } from './ElementTypeRenderers/Select'

type VisualizationProps = CustomVisualizationProps<ReturnType<typeof useFormInsightsDataSource>>
type Props = VisualizationProps & {
  headerContent?: React.ReactNode
  materializedFormId?: string
}

export function InsightsVisualization({
  onLoadData,
  source,
  headerContent,
  materializedFormId,
}: Props) {
  const { data, isInitialLoading } = useDataCollectionData(source)

  const groupedElements = groupBy(data.records, (element) => element.sectionId || 'ungrouped')
  const firstLevelElements = sortBy(groupedElements['ungrouped'], 'position')

  useEffect(() => {
    onLoadData({
      totalItems: data.records.length, // TODO: Use pagination
      filters: source.currentFilters,
      search: source.currentSearch,
      isInitialLoading,
      data: data.records,
    })
  }, [data.records])

  return (
    <Box>
      {headerContent && (
        <Box width="full" alignItems="center" paddingTop="s16">
          <Box maxWidth="narrowPage" width="full" paddingX="s16">
            {headerContent}
          </Box>
        </Box>
      )}

      <Box width="full" alignItems="center" flexGrow>
        <Box maxWidth="narrowPage" gap="s32" width="full" paddingLeft="s16" paddingTop="s16">
          {firstLevelElements.filter(Boolean).map((element) => {
            if (element.type === 'section') {
              return (
                <Section key={element.id} element={element}>
                  {groupedElements[element.id]?.map((subElement) =>
                    renderElement(
                      subElement,
                      subElement.id,
                      source.currentFilters,
                      materializedFormId
                    )
                  )}
                </Section>
              )
            }

            return renderElement(element, element.id, source.currentFilters, materializedFormId)
          })}
        </Box>
      </Box>
    </Box>
  )
}

function renderElement(
  element: FormInsightsOverviewElementFragment,
  key: string,
  activeFilters: FormFiltersStorage['filters'],
  materializedFormId?: string
) {
  if (!element || !element.type) {
    return null
  }
  const elementKey = key

  switch (element.type) {
    case 'select':
    case 'multiselect':
      return (
        <Select
          key={elementKey}
          element={element}
          activeFilters={activeFilters}
          materializedFormId={materializedFormId}
        />
      )
    case 'rating':
      return (
        <Rating
          key={elementKey}
          element={element}
          activeFilters={activeFilters}
          materializedFormId={materializedFormId}
        />
      )
    case 'number':
      return (
        <NumberElement
          key={elementKey}
          element={element}
          activeFilters={activeFilters}
          materializedFormId={materializedFormId}
        />
      )
    case 'longtext':
    case 'text':
      return (
        <LongText
          // Force remount on filter changes to reset state and require explicit user action
          key={`${elementKey}-${materializedFormId}-${JSON.stringify(activeFilters)}`}
          element={element}
          activeFilters={activeFilters}
          materializedFormId={materializedFormId}
        />
      )
    case 'score':
      return null
    case 'section':
      return null
    default:
      return null
  }
}
